import { computed, ref, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type {
  NodeWarning,
  WarningValidationContext,
  WarningValidator,
  CampaignType,
} from '@/types/flow-types';
import { TypeCampaignEnum } from '@/types';
import type { FlowStructureDto } from '@/types/flow-structure';
import { parseOperatorParameters } from '@/helpers/flowParameters';
import { getOperatorById } from '@/components/flows/operators/registry';
import { getEffectiveOperatorId } from '@/composables/flows/compatibility/effective-id';

/**
 * Global validators that apply to ALL operators
 * These check things like incompatibilities, campaign type availability, etc.
 */
const globalValidators: WarningValidator<any>[] = [
  {
    id: 'campaign-type-availability',
    validate: (data: any, context: WarningValidationContext): NodeWarning | null => {
      const operator = getOperatorById(data.id);
      if (!operator || operator.availableIn.includes(context.scenarioType)) {
        return null;
      }
      // Deprecated operator (no campaign type left) → friendlier copy with optional per-operator override.
      // Filters all migrate to "Conditions logiques" so they share a dedicated message.
      // Otherwise the operator still exists elsewhere → keep the legacy "not available in this type" wording.
      const isDeprecated = operator.availableIn.length === 0;
      const isDeprecatedFilter = isDeprecated && operator.kind === 'filter';
      const deprecatedKey = operator.kind === 'filter'
        ? 'automatedScenarios.tooltips.elementDeprecatedFilter'
        : 'automatedScenarios.tooltips.elementDeprecated';
      const message = operator.deprecatedMessage
        ?? (isDeprecated
          ? deprecatedKey
          : 'automatedScenarios.tooltips.elementNotAvailable');
      return {
        id: 'campaign-type-availability',
        severity: isDeprecatedFilter ? 'warning' : 'error',
        location: 'box',
        message,
        blocking: !isDeprecatedFilter,
      };
    },
  },
  {
    id: 'form-incomplete',
    validate: async (data: any, context: WarningValidationContext): Promise<NodeWarning | null> => {
      const operator = getOperatorById(data.id);
      if (operator?.validate && operator.hasConfiguration) {
        try {
          const result = await operator.validate(data, { shopId: context.shopId });
          if (!result.success) {
            return {
              id: 'form-incomplete',
              severity: 'error',
              location: 'box',
              message: 'automatedScenarios.tooltips.operatorIncompleteConfiguration',
              blocking: true,
            };
          }
        } catch (error) {
          console.error(`[Warnings] Validation error for ${data.id}:`, error);
        }
      }
      return null;
    },
  },
  {
    id: 'static-incompatibility',
    validate: (data: any, context: WarningValidationContext): NodeWarning | NodeWarning[] | null => {
      const incompatibleWarnings: NodeWarning[] = [];
      // Resolve both sides to their effective id so internal-event triggers are
      // compared by their event (visit_page, pass_time…) and not by the shared
      // `boxtriggerinternalevent` wrapper id.
      const childId = getEffectiveOperatorId(data);
      for (const parent of context.parentOperators) {
        const parentOperator = getOperatorById(getEffectiveOperatorId(parent.data ?? { id: parent.type }));
        const incompatibilities = parentOperator?.incompatibilities;
        if (incompatibilities) {
          const campaignIncompat = incompatibilities[context.scenarioType as keyof typeof incompatibilities];
          if (campaignIncompat?.nextOperators?.includes(childId)) {
            incompatibleWarnings.push({
              id: 'static-incompatibility',
              severity: 'error',
              location: 'branch',
              message: 'flows.warnings.operatorIncompatibility',
              blocking: true,
              parentId: parent.id,
            });
          }
        }
      }
      return incompatibleWarnings.length > 0 ? incompatibleWarnings : null;
    },
  },
  {
    id: 'no-target-contacts',
    validate: (data: any, context: WarningValidationContext): NodeWarning | null => {
      // Only for bulk campaigns
      if (context.scenarioType !== TypeCampaignEnum.BULK) {
        return null;
      }
      if (context.targetCalculation && context.targetCalculation !== 'loading') {
        if (context.targetCalculation.in === 0) {
          return {
            id: 'no-target-contacts',
            severity: 'warning',
            location: 'box',
            message: 'automatedScenarios.tooltips.noTargetFound',
            blocking: false,
          };
        }
      }
      return null;
    },
  },
];

/**
 * Composable for managing node warnings
 */
export function useNodeWarnings() {
  const { t } = useI18n();

  /**
   * Validate a single operator and return all warnings
   */
  const validateOperator = async (
    operatorId: string,
    operatorData: any,
    context: Omit<WarningValidationContext, 'operatorId' | 'data'>
  ): Promise<NodeWarning[]> => {
    const warnings: NodeWarning[] = [];
    const fullContext: WarningValidationContext = {
      ...context,
      operatorId,
      data: operatorData,
    };

    // Get operator metadata
    const operator = getOperatorById(operatorData.id);

    // Run global validators
    for (const validator of globalValidators) {
      try {
        const result = await validator.validate(operatorData, fullContext);
        if (result) {
          if (Array.isArray(result)) {
            warnings.push(...result);
          } else {
            warnings.push(result);
          }
        }
      } catch (error) {
        console.warn(`Global validator ${validator.id} failed:`, error);
      }
    }

    // Run operator-specific validators
    if (operator?.validators) {
      for (const validator of operator.validators) {
        try {
          const result = await validator.validate(operatorData, fullContext);
          if (result) {
            if (Array.isArray(result)) {
              warnings.push(...result);
            } else {
              warnings.push(result);
            }
          }
        } catch (error) {
          console.warn(`Operator validator ${validator.id} failed:`, error);
        }
      }
    }

    return warnings;
  };

  /**
   * Check if any warning is blocking
   */
  const hasBlockingWarning = (warnings: NodeWarning[]): boolean => {
    return warnings.some((w) => w.blocking);
  };

  /**
   * Validate an entire flow structure off-screen (no rendered nodes) and return
   * the steps carrying blocking warnings. Mirrors FlowRenderer's per-step
   * validation so activation can be gated from the campaign list.
   */
  const getFlowBlockingWarnings = async (
    flow: FlowStructureDto,
    scenarioType: CampaignType,
    shopId?: number,
  ): Promise<{ stepId: string; warnings: NodeWarning[] }[]> => {
    const results = await Promise.all(
      flow.steps.map(async (step) => {
        const directParents = step.parents && step.parents.length > 0 ? step.parents : [];
        const allParents = directParents.length > 0 || !step.primaryParent
          ? directParents
          : [step.primaryParent];

        const parentOperators = allParents.reduce<Array<{ id: string; type: string; data: any }>>(
          (acc, parentRef) => {
            const parentStep = flow.steps.find((s) => s.id === parentRef.id);
            if (parentStep) {
              acc.push({
                id: parentStep.id,
                type: parentStep.type,
                data: { id: parentStep.type, ...parseOperatorParameters(parentStep.parameters) },
              });
            }
            return acc;
          },
          [],
        );

        const warnings = await validateOperator(
          step.id,
          { id: step.type, ...parseOperatorParameters(step.parameters) },
          {
            scenarioType,
            parentIds: parentOperators.map((p) => p.id),
            parentOperators,
            shopId,
            // Needed by chain-walking validators (isOnPeriodicChain) — mirrors the editor.
            flow,
          },
        );

        return { stepId: step.id, warnings: warnings.filter((w) => w.blocking) };
      }),
    );

    return results.filter((result) => result.warnings.length > 0);
  };

  /**
   * Get the highest severity from a list of warnings
   */
  const getHighestSeverity = (warnings: NodeWarning[]): 'error' | 'warning' | 'info' | null => {
    if (warnings.length === 0) return null;
    if (warnings.some((w) => w.severity === 'error')) return 'error';
    if (warnings.some((w) => w.severity === 'warning')) return 'warning';
    return 'info';
  };

  /**
   * Format warning message with i18n
   */
  const formatWarningMessage = (warning: NodeWarning): string => {
    return t(warning.message, warning.messageParams || {});
  };

  /**
   * Format all warnings as HTML list (for tooltip)
   */
  const formatWarningsAsHtml = (warnings: NodeWarning[]): string => {
    if (warnings.length === 0) return '';
    const items = warnings.map((w) => `<li>- ${formatWarningMessage(w)}</li>`);
    return `<ul>${items.join('')}</ul>`;
  };

  return {
    validateOperator,
    hasBlockingWarning,
    getFlowBlockingWarnings,
    getHighestSeverity,
    formatWarningMessage,
    formatWarningsAsHtml,
    globalValidators,
  };
}

/**
 * Create a reactive warnings state for a single node
 */
export function useNodeWarningsState(
  operatorId: Ref<string>,
  operatorData: Ref<any>,
  context: Ref<Omit<WarningValidationContext, 'operatorId' | 'data'>>
) {
  const { validateOperator, hasBlockingWarning, getHighestSeverity, formatWarningsAsHtml } = useNodeWarnings();

  const warnings: Ref<NodeWarning[]> = ref([]);
  const isValidating = ref(false);

  const validate = async () => {
    isValidating.value = true;
    try {
      warnings.value = await validateOperator(
        operatorId.value,
        operatorData.value,
        context.value
      );
    } finally {
      isValidating.value = false;
    }
  };

  const hasWarnings = computed(() => warnings.value.length > 0);
  const isBlocking = computed(() => hasBlockingWarning(warnings.value));
  const severity = computed(() => getHighestSeverity(warnings.value));
  const tooltipHtml = computed(() => formatWarningsAsHtml(warnings.value));

  return {
    warnings,
    isValidating,
    validate,
    hasWarnings,
    isBlocking,
    severity,
    tooltipHtml,
  };
}
